import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, FormControl, FormLabel, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faMagicWandSparkles, faRotate, faSave } from "@fortawesome/free-solid-svg-icons";
import { IMediaItem, ISiteDetails } from "../../../models/Interfaces";
import { editAiImage, generateAiImage } from "../../../AIHelper/aiImageClient";
import { MediaService } from "../../../services/MediaService";
import ImageEditor from "../../components/common/ImageEditor";
import styles from "./mediaUploadPreview.module.scss";

export type AiImageDialogMode = "generate" | "edit";

interface IAiImageDialogProps {
  mode: AiImageDialogMode;
  site: ISiteDetails;
  folderId: number;
  sourceFile?: IMediaItem | null;
  onClose: () => void;
  onUseImage: (file: File) => void;
  onReplace: (file: File) => Promise<boolean>;
  onSaveAsNew: (file: File) => Promise<boolean>;
}

const fileFromBlob = (blob: Blob, filename: string): File => {
  const type = blob.type || "image/png";
  return new File([blob], filename, { type });
};

const AiImageDialog = ({
  mode,
  site,
  folderId,
  sourceFile,
  onClose,
  onUseImage,
  onReplace,
  onSaveAsNew
}: IAiImageDialogProps): JSX.Element => {
  const [prompt, setPrompt] = useState<string>("");
  const [previewUrl, setPreviewUrl] = useState<string>(mode === "edit" ? (sourceFile?.Url ?? "") : "");
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState<boolean>(false);
  const [loadingSource, setLoadingSource] = useState<boolean>(false);
  const [saveAction, setSaveAction] = useState<"replace" | "new" | null>(null);
  const saving = saveAction !== null;
  const [adjusting, setAdjusting] = useState<boolean>(false);
  const [fullScreen, setFullScreen] = useState<boolean>(false);
  const requestId = useRef<number>(0);

  useEffect(() => {
    return () => {
      requestId.current += 1;
    };
  }, []);

  const showResult = (blob: Blob): void => {
    setPreviewUrl(URL.createObjectURL(blob));
    setPreviewBlob(blob);
    setAdjusting(false);
  };

  useEffect(() => {
    return () => {
      if (previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const editorItem = useMemo<IMediaItem | null>(() => {
    if (!previewUrl) {
      return null;
    }

    return {
      SiteId: sourceFile?.SiteId ?? "",
      Id: sourceFile?.Id ?? 0,
      Filename: sourceFile?.Filename ?? "ai-image.png",
      DateAdded: new Date().toISOString(),
      AddedBy: "",
      Url: previewUrl,
      FileType: "image",
      FileData: null,
      ContentType: previewBlob?.type || "image/png",
      FolderId: folderId,
      FolderName: sourceFile?.FolderName ?? "",
      FolderLevel: sourceFile?.FolderLevel ?? 0,
      SourceFileId: null,
      MetadataJson: null,
      SrcSet: [],
      IsVideo: false
    };
  }, [folderId, previewBlob, previewUrl, sourceFile]);

  const closeDialog = (): void => {
    if (generating || saving || loadingSource) {
      return;
    }

    requestId.current += 1;
    onClose();
  };

  // Storage lets an image tag display the public address, but it does not send CORS headers.
  // Reading the bytes in the browser has to go through the admin API.
  const loadSourceBlob = async (): Promise<Blob> => {
    if (previewBlob) {
      return previewBlob;
    }

    if (sourceFile?.Id) {
      return await new MediaService().downloadFileContent(sourceFile.Id);
    }

    if (sourceFile?.Url?.startsWith("blob:") || sourceFile?.Url?.startsWith("data:")) {
      const response = await fetch(sourceFile.Url);
      if (!response.ok) {
        throw new Error("The current image could not be loaded for editing.");
      }

      return await response.blob();
    }

    throw new Error("The current image could not be loaded for editing.");
  };

  const handleAdjust = async (): Promise<void> => {
    if (adjusting) {
      setAdjusting(false);
      return;
    }

    if (previewBlob) {
      setAdjusting(true);
      return;
    }

    setLoadingSource(true);
    setError(null);
    try {
      const blob = await loadSourceBlob();
      setPreviewBlob(blob);
      setPreviewUrl(URL.createObjectURL(blob));
      setAdjusting(true);
    } catch (adjustError) {
      const message = adjustError instanceof Error ? adjustError.message : "The current image could not be loaded for editing.";
      setError(message);
    } finally {
      setLoadingSource(false);
    }
  };

  const handleGenerate = async (): Promise<void> => {
    const description = prompt.trim();
    if (!description || generating || saving || loadingSource) {
      return;
    }

    const id = ++requestId.current;
    setGenerating(true);
    setError(null);

    try {
      const result = mode === "edit"
        ? await editAiImage(site, description, await loadSourceBlob())
        : await generateAiImage(site, description);

      if (id !== requestId.current) {
        return;
      }

      if (result.ok && result.blob) {
        showResult(result.blob);
      } else {
        setError(result.error || "The image could not be created.");
      }
    } catch (generateError) {
      if (id !== requestId.current) {
        return;
      }

      const message = generateError instanceof Error ? generateError.message : "The image could not be created.";
      setError(message);
    } finally {
      if (id === requestId.current) {
        setGenerating(false);
      }
    }
  };

  const resultFile = (): File | null => {
    if (!previewBlob) {
      return null;
    }

    const extension = previewBlob.type === "image/jpeg" ? "jpg" : "png";
    return fileFromBlob(previewBlob, `ai-${Date.now()}.${extension}`);
  };

  const handleUseImage = (): void => {
    const file = resultFile();
    if (!file) {
      return;
    }

    onUseImage(file);
    requestId.current += 1;
    onClose();
  };

  const handleReplace = async (): Promise<void> => {
    const file = resultFile();
    if (!file || saving || generating) {
      return;
    }

    setSaveAction("replace");
    setError(null);
    try {
      const replaced = await onReplace(file);
      if (replaced) {
        requestId.current += 1;
        onClose();
      }
    } catch (replaceError) {
      const message = replaceError instanceof Error ? replaceError.message : "The file could not be replaced.";
      setError(message);
    } finally {
      setSaveAction(null);
    }
  };

  const handleSaveAsNew = async (): Promise<void> => {
    const file = resultFile();
    if (!file || saving || generating) {
      return;
    }

    setSaveAction("new");
    setError(null);
    try {
      const saved = await onSaveAsNew(file);
      if (saved) {
        requestId.current += 1;
        onClose();
      }
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : "The file could not be saved.";
      setError(message);
    } finally {
      setSaveAction(null);
    }
  };

  const busy = generating || saving || loadingSource;
  const title = mode === "edit" ? "Edit with AI" : "Generate AI Image";
  const promptLabel = mode === "edit" ? "Describe the change" : "Describe the image";

  return (
    <Modal show onHide={closeDialog} size="xl" backdrop="static" fullscreen={fullScreen ? true : undefined}>
      <ModalHeader closeButton className="m-0 p-0 ps-3 pe-3 pt-1">
        <h5>{title}</h5>
      </ModalHeader>
      <ModalBody className={styles.aiDialogBody}>
        <div className={styles.aiIntro}>
          <div className={styles.aiIntroTitle}>{title}</div>
          <div className={styles.aiIntroText}>
            {mode === "edit"
              ? "The current image is the starting point. Describe the change, then replace this file or save the result as a new file in this folder."
              : "Describe the image, then generate again or adjust the preview. Use this image adds it to the pending uploads for this folder."}
          </div>
        </div>

        <FormLabel htmlFor="ai-image-prompt" className="mt-3">{promptLabel}</FormLabel>
        <FormControl
          id="ai-image-prompt"
          as="textarea"
          rows={3}
          value={prompt}
          disabled={busy}
          placeholder={mode === "edit" ? "Make the background a soft grey and keep the subject" : "A square photo of a ceramic mug on a wooden table"}
          onChange={(event) => setPrompt(event.target.value)}
        />
        <div className={styles.aiGenerateRow}>
          <Button variant="primary" type="button" onClick={handleGenerate} disabled={busy || prompt.trim().length === 0}>
            {generating
              ? <Spinner animation="border" size="sm" className="me-2" />
              : <FontAwesomeIcon icon={faMagicWandSparkles} className="me-2" />}
            {generating ? "Generating..." : "Generate"}
          </Button>
          {previewUrl && (
            <Button variant="outline-secondary" type="button" onClick={handleAdjust} disabled={busy}>
              <FontAwesomeIcon icon={faRotate} className="me-2" />
              {adjusting ? "Show preview" : "Adjust image"}
            </Button>
          )}
        </div>

        {error && (
          <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>
        )}

        <div className={styles.aiPreviewFrame}>
          {adjusting && editorItem ? (
            <div className={styles.aiEditor}>
              <ImageEditor
                mediaItem={editorItem}
                mimeType="image/png"
                onApplyChanges={(blob) => showResult(blob)}
                onFullScreenToggle={(fullscreen) => setFullScreen(fullscreen)}
              />
            </div>
          ) : previewUrl ? (
            <img src={previewUrl} alt={title} className={styles.aiPreview} />
          ) : (
            <div className={styles.aiPreviewEmpty}>The preview appears here after you generate an image.</div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        {mode === "generate" && (
          <Button variant="success" type="button" onClick={handleUseImage} disabled={!previewBlob || busy}>
            <FontAwesomeIcon icon={faCheck} className="me-2" />
            Use this image
          </Button>
        )}
        {mode === "edit" && (
          <>
            <Button variant="success" type="button" onClick={handleReplace} disabled={!previewBlob || busy}>
              {saveAction === "replace"
                ? <Spinner animation="border" size="sm" className="me-2" />
                : <FontAwesomeIcon icon={faSave} className="me-2" />}
              Replace this file
            </Button>
            <Button variant="outline-primary" type="button" onClick={handleSaveAsNew} disabled={!previewBlob || busy}>
              {saveAction === "new"
                ? <Spinner animation="border" size="sm" className="me-2" />
                : <FontAwesomeIcon icon={faSave} className="me-2" />}
              Save as new file
            </Button>
          </>
        )}
        <Button variant="outline-secondary" type="button" onClick={closeDialog} disabled={busy}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AiImageDialog;
